export type JSONValue = null | boolean | number | string | JSONValue[] | { [key: string]: JSONValue };

export interface SemanticNode {
  id: string;
  kind: string;
  content: Record<string, JSONValue>;
  metadata: Record<string, JSONValue>;
}

export interface SemanticRelation {
  id: string;
  kind: string;
  from: string;
  to: string;
  metadata: Record<string, JSONValue>;
}

export interface SemanticSequence {
  parent: string;
  name: string;
  nodes: string[];
}

export interface Projection {
  id: string;
  document_id: string;
  source_version: string;
  digest: string;
  nodes: SemanticNode[];
  relations: SemanticRelation[];
  sequences: SemanticSequence[];
}

interface IdentityDraft {
  id: string;
  name: string;
  headline: string;
}

interface ContactDraft {
  id: string;
  email: string;
  phone: string;
  location: string;
  website: string;
}

interface ProfileDraft {
  id: string;
  summary: string;
}

export interface ExperienceDraft {
  id: string;
  organization: string;
  role: string;
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  summary: string;
  bullets: string[];
}

export interface EducationDraft {
  id: string;
  institution: string;
  qualification: string;
  field: string;
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  details: string;
}

export interface SkillDraft {
  id: string;
  name: string;
  level: string;
  keywords: string[];
}

export interface ProjectDraft {
  id: string;
  name: string;
  description: string;
  url: string;
  startDate: string;
  endDate: string;
  bullets: string[];
}

export interface ResumeDraft {
  rootId: string;
  locale: string;
  identity: IdentityDraft;
  contact: ContactDraft;
  profile: ProfileDraft;
  experienceContainerId: string;
  experiences: ExperienceDraft[];
  educationContainerId: string;
  education: EducationDraft[];
  skillsContainerId: string;
  skills: SkillDraft[];
  projects: ProjectDraft[];
}

export interface InitialSemanticState {
  nodes: SemanticNode[];
  relations: SemanticRelation[];
  sequences: SemanticSequence[];
}

export type PatchOperation =
  | { op: "node.add"; node: SemanticNode }
  | { op: "node.remove"; target: string; cascade: boolean }
  | { op: "node.content.set"; target: string; path: string; value: JSONValue }
  | { op: "relation.add"; relation: SemanticRelation }
  | { op: "sequence.move"; parent: string; child: string; sequence: string; position: "first" | "last" }
  | { op: "sequence.move"; parent: string; child: string; sequence: string; after: string };

const emptyMetadata: Record<string, JSONValue> = {};

export function createBlankDraft(): ResumeDraft {
  return {
    rootId: newID("node"),
    locale: navigatorLanguage(),
    identity: { id: newID("node"), name: "", headline: "" },
    contact: { id: newID("node"), email: "", phone: "", location: "", website: "" },
    profile: { id: newID("node"), summary: "" },
    experienceContainerId: newID("node"),
    experiences: [],
    educationContainerId: newID("node"),
    education: [],
    skillsContainerId: newID("node"),
    skills: [],
    projects: [],
  };
}

export function createExperience(): ExperienceDraft {
  return { id: newID("node"), organization: "", role: "", location: "", startDate: "", endDate: "", current: false, summary: "", bullets: [] };
}

export function createEducation(): EducationDraft {
  return { id: newID("node"), institution: "", qualification: "", field: "", location: "", startDate: "", endDate: "", current: false, details: "" };
}

export function createSkill(): SkillDraft {
  return { id: newID("node"), name: "", level: "", keywords: [] };
}

export function createProject(): ProjectDraft {
  return { id: newID("node"), name: "", description: "", url: "", startDate: "", endDate: "", bullets: [] };
}

const resumeEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const resumeDatePattern = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;

function emailError(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/\s/.test(value)) return "Remove spaces. Use an address such as name@example.com.";
  if (!trimmed.includes("@")) return "Add @ and a domain, for example name@example.com.";
  if (!resumeEmailPattern.test(trimmed)) return "Use a complete address such as name@example.com.";
  return undefined;
}

function urlError(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    const parsed = new URL(trimmed);
    if ((parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname) return undefined;
  } catch {
    // Return the same bounded guidance for every malformed URL.
  }
  return "Enter a full address starting with https:// (or http://).";
}

function parsedResumeDate(value: string): number | undefined {
  const match = resumeDatePattern.exec(value);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = match[2] ? Number(match[2]) : 1;
  const day = match[3] ? Number(match[3]) : 1;
  if (year < 1900 || year > 2200 || month < 1 || month > 12) return undefined;
  const maxDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day < 1 || day > maxDay) return undefined;
  return year * 372 + month * 31 + day;
}

function addDateErrors(
  errors: Record<string, string>,
  id: string,
  startDate: string,
  endDate: string,
): void {
  const formatGuidance = "Use YYYY, YYYY-MM, or YYYY-MM-DD (for example 2024-09).";
  const start = startDate ? parsedResumeDate(startDate) : undefined;
  const end = endDate ? parsedResumeDate(endDate) : undefined;
  if (startDate && start === undefined) errors[`${id}.startDate`] = formatGuidance;
  if (endDate && end === undefined) errors[`${id}.endDate`] = formatGuidance;
  if (start !== undefined && end !== undefined && end < start) {
    errors[`${id}.endDate`] = "End date cannot be earlier than start date.";
  }
}

export function validateDraftFormats(draft: ResumeDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  const email = emailError(draft.contact.email);
  const website = urlError(draft.contact.website);
  if (email) errors[`${draft.contact.id}.email`] = email;
  if (website) errors[`${draft.contact.id}.website`] = website;
  for (const item of draft.experiences) addDateErrors(errors, item.id, item.startDate, item.endDate);
  for (const item of draft.education) addDateErrors(errors, item.id, item.startDate, item.endDate);
  for (const item of draft.projects) {
    const projectURL = urlError(item.url);
    if (projectURL) errors[`${item.id}.url`] = projectURL;
    addDateErrors(errors, item.id, item.startDate, item.endDate);
  }
  return errors;
}

export function validateDraft(draft: ResumeDraft): Record<string, string> {
  const errors = validateDraftFormats(draft);
  if (!draft.identity.name.trim()) errors[`${draft.identity.id}.name`] = "Add your name.";
  if (!draft.profile.summary.trim()) errors[`${draft.profile.id}.summary`] = "Add a short professional profile.";
  for (const item of draft.experiences) {
    if (!item.organization.trim()) errors[`${item.id}.organization`] = "Add an organization.";
    if (!item.role.trim()) errors[`${item.id}.role`] = "Add a role.";
    if (!item.startDate.trim()) errors[`${item.id}.startDate`] = "Add a start date.";
    if (item.current && item.endDate.trim()) errors[`${item.id}.endDate`] = "A current role cannot have an end date.";
  }
  for (const item of draft.education) {
    if (!item.institution.trim()) errors[`${item.id}.institution`] = "Add an institution.";
    if (!item.qualification.trim()) errors[`${item.id}.qualification`] = "Add a qualification.";
  }
  for (const item of draft.skills) {
    if (!item.name.trim()) errors[`${item.id}.name`] = "Add a skill.";
  }
  for (const item of draft.projects) {
    if (!item.name.trim()) errors[`${item.id}.name`] = "Add a project name.";
  }
  return errors;
}

export function buildInitialState(draft: ResumeDraft): InitialSemanticState {
  const nodes: SemanticNode[] = [
    node(draft.rootId, "resume.root", compact({ title: "Resume", locale: draft.locale })),
    node(draft.identity.id, "resume.identity", identityContent(draft.identity)),
    node(draft.contact.id, "resume.contact", contactContent(draft.contact)),
    node(draft.profile.id, "resume.profile", profileContent(draft.profile)),
    node(draft.experienceContainerId, "resume.experience", {}),
    node(draft.educationContainerId, "resume.education", {}),
    node(draft.skillsContainerId, "resume.skills", {}),
    ...draft.experiences.map((item) => node(item.id, "resume.experience_item", experienceContent(item))),
    ...draft.education.map((item) => node(item.id, "resume.education_item", educationContent(item))),
    ...draft.skills.map((item) => node(item.id, "resume.skill", skillContent(item))),
    ...draft.projects.map((item) => node(item.id, "resume.project", projectContent(item))),
  ];
  const topLevel = [draft.identity.id, draft.contact.id, draft.profile.id, draft.experienceContainerId, draft.educationContainerId, draft.skillsContainerId, ...draft.projects.map((item) => item.id)];
  const pairs: Array<[string, string]> = [
    ...topLevel.map((child): [string, string] => [draft.rootId, child]),
    ...draft.experiences.map((item): [string, string] => [draft.experienceContainerId, item.id]),
    ...draft.education.map((item): [string, string] => [draft.educationContainerId, item.id]),
    ...draft.skills.map((item): [string, string] => [draft.skillsContainerId, item.id]),
  ];
  return {
    nodes,
    relations: pairs.map(([from, to]) => relation(from, to)),
    sequences: [
      { parent: draft.rootId, name: "sections", nodes: topLevel },
      { parent: draft.experienceContainerId, name: "items", nodes: draft.experiences.map((item) => item.id) },
      { parent: draft.educationContainerId, name: "items", nodes: draft.education.map((item) => item.id) },
      { parent: draft.skillsContainerId, name: "items", nodes: draft.skills.map((item) => item.id) },
    ],
  };
}

export function draftFromProjection(projection: Projection): ResumeDraft {
  const byKind = new Map<string, SemanticNode[]>();
  for (const item of projection.nodes) byKind.set(item.kind, [...(byKind.get(item.kind) ?? []), item]);
  const one = (kind: string): SemanticNode => {
    const item = byKind.get(kind)?.[0];
    if (!item) throw new Error(`Stored resume is missing ${kind}.`);
    return item;
  };
  const ordered = (parent: string, kind: string): SemanticNode[] => {
    const ids = projection.sequences.find((sequence) => sequence.parent === parent && sequence.name === "items")?.nodes ?? [];
    const index = new Map(projection.nodes.map((item) => [item.id, item]));
    const sequenced = ids.map((id) => index.get(id)).filter((item): item is SemanticNode => item?.kind === kind);
    const known = new Set(sequenced.map((item) => item.id));
    return [...sequenced, ...(byKind.get(kind) ?? []).filter((item) => !known.has(item.id)).sort((a, b) => a.id.localeCompare(b.id))];
  };
  const root = one("resume.root");
  const identity = one("resume.identity");
  const contact = one("resume.contact");
  const profile = one("resume.profile");
  const experience = one("resume.experience");
  const education = one("resume.education");
  const skills = one("resume.skills");
  return {
    rootId: root.id,
    locale: text(root.content.locale) || "en",
    identity: { id: identity.id, name: text(identity.content.name), headline: text(identity.content.headline) },
    contact: { id: contact.id, email: text(contact.content.email), phone: text(contact.content.phone), location: text(contact.content.location), website: text(contact.content.website) },
    profile: { id: profile.id, summary: text(profile.content.summary) },
    experienceContainerId: experience.id,
    experiences: ordered(experience.id, "resume.experience_item").map(experienceFromNode),
    educationContainerId: education.id,
    education: ordered(education.id, "resume.education_item").map(educationFromNode),
    skillsContainerId: skills.id,
    skills: ordered(skills.id, "resume.skill").map(skillFromNode),
    projects: (byKind.get("resume.project") ?? []).sort((a, b) => a.id.localeCompare(b.id)).map(projectFromNode),
  };
}

export function buildPatchOperations(projection: Projection, draft: ResumeDraft): PatchOperation[] {
  const original = new Map(projection.nodes.map((item) => [item.id, item]));
  const operations: PatchOperation[] = [];
  updateExisting(operations, original, draft.identity.id, identityContent(draft.identity));
  updateExisting(operations, original, draft.contact.id, contactContent(draft.contact));
  updateExisting(operations, original, draft.profile.id, profileContent(draft.profile));
  syncCollection(operations, projection, original, "resume.experience_item", draft.experiences.map((item) => ({ id: item.id, content: experienceContent(item) })), draft.experienceContainerId, "items");
  syncCollection(operations, projection, original, "resume.education_item", draft.education.map((item) => ({ id: item.id, content: educationContent(item) })), draft.educationContainerId, "items");
  syncCollection(operations, projection, original, "resume.skill", draft.skills.map((item) => ({ id: item.id, content: skillContent(item) })), draft.skillsContainerId, "items");
  syncCollection(operations, projection, original, "resume.project", draft.projects.map((item) => ({ id: item.id, content: projectContent(item) })), draft.rootId, "sections", draft.skillsContainerId);
  return operations;
}

function syncCollection(
  operations: PatchOperation[],
  projection: Projection,
  original: Map<string, SemanticNode>,
  kind: string,
  current: Array<{ id: string; content: Record<string, JSONValue> }>,
  parent: string,
  sequence: string,
  firstAfter?: string,
): void {
  const currentIDs = new Set(current.map((item) => item.id));
  for (const stored of original.values()) {
    if (stored.kind === kind && !currentIDs.has(stored.id)) operations.push({ op: "node.remove", target: stored.id, cascade: true });
  }
  for (const item of current) {
    if (original.has(item.id)) {
      updateExisting(operations, original, item.id, item.content);
    } else {
      operations.push({ op: "node.add", node: node(item.id, kind, compact(item.content)) });
      operations.push({ op: "relation.add", relation: relation(parent, item.id) });
    }
  }
  const storedOrder = (projection.sequences.find((item) => item.parent === parent && item.name === sequence)?.nodes ?? []).filter((id) => original.get(id)?.kind === kind && currentIDs.has(id));
  const currentOrder = current.map((item) => item.id);
  if (JSON.stringify(storedOrder) !== JSON.stringify(currentOrder)) {
    currentOrder.forEach((child, index) => {
      const anchor = index === 0 ? firstAfter : currentOrder[index - 1];
      if (anchor) operations.push({ op: "sequence.move", parent, child, sequence, after: anchor });
      else operations.push({ op: "sequence.move", parent, child, sequence, position: "first" });
    });
  }
}

function updateExisting(operations: PatchOperation[], original: Map<string, SemanticNode>, id: string, next: Record<string, JSONValue>): void {
  const stored = original.get(id);
  if (!stored) throw new Error(`Stored resume is missing node ${id}.`);
  const keys = [...new Set([...Object.keys(stored.content), ...Object.keys(next)])].sort();
  for (const key of keys) {
    const value = next[key] ?? null;
    if (JSON.stringify(stored.content[key] ?? null) !== JSON.stringify(value)) {
      operations.push({ op: "node.content.set", target: id, path: `/${key}`, value });
    }
  }
}

function identityContent(value: IdentityDraft): Record<string, JSONValue> {
  return { name: value.name.trim(), headline: nullable(value.headline) };
}

function contactContent(value: ContactDraft): Record<string, JSONValue> {
  return { email: nullable(value.email), phone: nullable(value.phone), location: nullable(value.location), website: nullable(value.website) };
}

function profileContent(value: ProfileDraft): Record<string, JSONValue> {
  return { summary: value.summary.trim() };
}

function experienceContent(value: ExperienceDraft): Record<string, JSONValue> {
  return {
    organization: value.organization.trim(), role: value.role.trim(), location: nullable(value.location),
    start_date: value.startDate.trim(), end_date: value.current ? null : nullable(value.endDate), current: value.current,
    summary: nullable(value.summary), bullets: value.bullets.map((item) => item.trim()).filter(Boolean),
  };
}

function educationContent(value: EducationDraft): Record<string, JSONValue> {
  return {
    institution: value.institution.trim(), qualification: value.qualification.trim(), field: nullable(value.field),
    location: nullable(value.location), start_date: nullable(value.startDate), end_date: value.current ? null : nullable(value.endDate),
    current: value.current, details: nullable(value.details),
  };
}

function skillContent(value: SkillDraft): Record<string, JSONValue> {
  return { name: value.name.trim(), level: nullable(value.level), keywords: value.keywords.map((item) => item.trim()).filter(Boolean) };
}

function projectContent(value: ProjectDraft): Record<string, JSONValue> {
  return {
    name: value.name.trim(), description: nullable(value.description), url: nullable(value.url),
    start_date: nullable(value.startDate), end_date: nullable(value.endDate),
    bullets: value.bullets.map((item) => item.trim()).filter(Boolean),
  };
}

function experienceFromNode(item: SemanticNode): ExperienceDraft {
  return { id: item.id, organization: text(item.content.organization), role: text(item.content.role), location: text(item.content.location), startDate: text(item.content.start_date), endDate: text(item.content.end_date), current: item.content.current === true, summary: text(item.content.summary), bullets: strings(item.content.bullets) };
}

function educationFromNode(item: SemanticNode): EducationDraft {
  return { id: item.id, institution: text(item.content.institution), qualification: text(item.content.qualification), field: text(item.content.field), location: text(item.content.location), startDate: text(item.content.start_date), endDate: text(item.content.end_date), current: item.content.current === true, details: text(item.content.details) };
}

function skillFromNode(item: SemanticNode): SkillDraft {
  return { id: item.id, name: text(item.content.name), level: text(item.content.level), keywords: strings(item.content.keywords) };
}

function projectFromNode(item: SemanticNode): ProjectDraft {
  return { id: item.id, name: text(item.content.name), description: text(item.content.description), url: text(item.content.url), startDate: text(item.content.start_date), endDate: text(item.content.end_date), bullets: strings(item.content.bullets) };
}

function node(id: string, kind: string, content: Record<string, JSONValue>): SemanticNode {
  return { id, kind, content, metadata: { ...emptyMetadata } };
}

function relation(from: string, to: string): SemanticRelation {
  return { id: newID("rel"), kind: "contains", from, to, metadata: { ...emptyMetadata } };
}

function compact(value: Record<string, JSONValue>): Record<string, JSONValue> {
  return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== null)) as Record<string, JSONValue>;
}

function nullable(value: string): string | null {
  const normalized = value.trim();
  return normalized || null;
}

function text(value: JSONValue | undefined): string {
  return typeof value === "string" ? value : "";
}

function strings(value: JSONValue | undefined): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function navigatorLanguage(): string {
  return typeof navigator === "undefined" ? "en" : navigator.language;
}

export function newID(prefix: "node" | "rel"): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let timestamp = Date.now();
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = timestamp & 0xff;
    timestamp = Math.floor(timestamp / 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const encoded = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  const uuid = `${encoded.slice(0, 8)}-${encoded.slice(8, 12)}-${encoded.slice(12, 16)}-${encoded.slice(16, 20)}-${encoded.slice(20)}`;
  return `${prefix}_${uuid}`;
}
